
import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { RiDeleteBin6Line } from "@remixicon/react";
import { Button } from "@/web/components/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/web/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/web/components/ui/form";
import { Input } from "@/web/components/ui/input";
import type { Product } from "@/types";

const productFormScheme = z.object({
  name: z.string().min(3, "Product name is required"),
  description: z.string().optional(),
  barcode: z.string().optional(),
  packageSize: z.string().optional(),
  brand: z.string().optional(),
  amountAsCents: z.coerce.number(),
  saveAmountAsCents: z.coerce.number().optional(),
  wasAmountAsCents: z.coerce.number().optional(),
  quantity: z.coerce.number(),
  priceAsCents: z.coerce.number(),
  unitOfMeasure: z.string()
});

type ProductFormData = z.infer<typeof productFormScheme>;

export interface ProductFormModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: (productId: string) => void;
  onSubmit: (product: Product) => void;
  mode: "create" | "edit";
  isLoading?: boolean;
  product?: Product;
}

export function ProductFormModal({
  isOpen,
  onOpenChange,
  mode,
  isLoading,
  product,
  onDelete,
  onSubmit,
}) {
  const [productId, setProductId] = React.useState('');
  const form = useForm<ProductFormData>({
    resolver: zodResolver(productFormScheme),
    defaultValues: {
      name: product?.name || '',
      description: product?.description || '',
      barcode: product?.barcode || '',
      packageSize: product?.packageSize || '',
      brand: product?.brand || '',
      amountAsCents: product?.price?.amountAsCents || '',
      saveAmountAsCents: product?.price?.saveAmountAsCents,
      wasAmountAsCents: product?.price?.wasAmountAsCents,
      quantity: product?.price?.comparativePricing?.quantity,
      priceAsCents: product?.price?.comparativePricing?.priceAsCents,
      unitOfMeasure: product?.price?.comparativePricing?.unitOfMeasure || '',
    }
  });

  React.useEffect(() => {
    form.reset({
      name: product?.name || '',
      description: product?.description || '',
      barcode: product?.barcode || '',
      packageSize: product?.packageSize || '',
      brand: product?.brand || '',
      amountAsCents: product?.price?.amountAsCents,
      saveAmountAsCents: product?.price?.saveAmountAsCents,
      wasAmountAsCents: product?.price?.wasAmountAsCents || product?.price?.amountAsCents,
      quantity: product?.price?.comparativePricing?.quantity,
      priceAsCents: product?.price?.comparativePricing?.priceAsCents,
      unitOfMeasure: product?.price?.comparativePricing?.unitOfMeasure || '',
    });
    setProductId(product?.productId);
  }, [isOpen, product])

  const handleCancel = () => {
    onOpenChange(false);
  }

  const handleDelete = () => {
    if (product) {
      onDelete(product?.productId);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Create New Product" : "Edit Product"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Add a new category to your database."
              : "Make changes to the category information."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormItem>
              <FormLabel>ProductId</FormLabel>
              <div>{productId}</div>
            </FormItem>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter product name"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amountAsCents"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Price</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="price"
                      type="number"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="wasAmountAsCents"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Was Amount</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter the previous price"
                      type="number"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="saveAmountAsCents"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Savings Amount</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter the savings amount"
                      type="number"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comparative Quantity</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter comparative quantity"
                      type="number"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="priceAsCents"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comprative Price</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter comparative price"
                      type="number"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="unitOfMeasure"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comparative UOM</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter unit of measure"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              {mode === "edit" && onDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={isLoading}
                  className="self-start"
                >
                  <RiDeleteBin6Line className="size-4" />
                </Button>
              )}

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading
                    ? mode === "create"
                      ? "Creating..."
                      : "Saving..."
                    : mode === "create"
                      ? "Create"
                      : "Update"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
